export interface AuthorNameParts {
  author_display_name?: string | null;
  author_first_name?: string | null;
  author_last_initial?: string | null;
  legacy_label?: string | null;
}

function normalizeText(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isIdLikeLabel(value: string): boolean {
  const normalized = value.trim();
  if (!normalized) {
    return true;
  }

  if (/^(dietitian|user)-[a-z0-9-]+$/i.test(normalized)) {
    return true;
  }

  if (/^member[\s._-]*[a-z0-9-]+$/i.test(normalized)) {
    return true;
  }

  if (/^[a-z0-9-]{16,}$/i.test(normalized)) {
    return true;
  }

  if (/^[a-z0-9]{4,}$/i.test(normalized) && /[a-z]/i.test(normalized) && /\d/.test(normalized)) {
    return true;
  }

  return false;
}

function sanitizeForDisplay(value: string | null | undefined): string | null {
  const normalized = normalizeText(value);
  if (!normalized) {
    return null;
  }
  return isIdLikeLabel(normalized) ? null : normalized;
}

function normalizeInitial(value: string | null | undefined): string | null {
  const raw = normalizeText(value);
  if (!raw) {
    return null;
  }
  const compact = raw.replace(/[^a-z0-9]/gi, "");
  if (!compact) {
    return null;
  }
  const firstChar = compact[0].toUpperCase();
  return /^[A-Z0-9]$/.test(firstChar) ? `${firstChar}.` : null;
}

export function resolvePublicAuthorName(parts: AuthorNameParts, fallback = "Anonymous"): string {
  const displayName = sanitizeForDisplay(parts.author_display_name);
  if (displayName) {
    return displayName;
  }

  const firstName = sanitizeForDisplay(parts.author_first_name);
  const lastInitial = normalizeInitial(parts.author_last_initial);
  if (firstName && lastInitial) {
    return `${firstName} ${lastInitial}`;
  }
  if (firstName) {
    return firstName;
  }

  const legacyLabel = sanitizeForDisplay(parts.legacy_label);
  if (legacyLabel) {
    return legacyLabel;
  }

  return fallback;
}

export function toOptimisticAuthorNameParts(name: string | null | undefined): AuthorNameParts {
  const normalized = normalizeText(name);
  if (!normalized) {
    return {};
  }

  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return {};
  }

  const firstName = words[0] ?? null;
  const finalWord = words[words.length - 1] ?? "";
  const maybeInitial = finalWord.length > 0 && words.length > 1 ? finalWord[0] : null;

  return {
    author_first_name: firstName,
    author_last_initial: maybeInitial,
  };
}
